import React, { useEffect, useState } from 'react'
import { IEventForm, IField, IFile, IMultiFile } from './constants'
import { ICarouselNavItems } from 'components/swiper/swiperNav'
import { CarouselHeader } from 'iebt-zing-storybook'
import { PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { trans } from 'sharedKernel/i18n'
import { FormChallengeHeader } from 'sharedKernel/styledComponents/stickHeader/style'
import { limitDataFields } from 'components/LimitDateFields'
import { IHubEventBasicFormState } from './store/contracts'
import { ButtonsWrapper, CardWrapper, DateRow, DividerLine, FormEventWrapper, SectionTitle, TimeRow, TitleAndButtonsWrapper, TitleWrapper } from './style'
import { SaveButton } from './SaveButton'
import { validateAll } from './behaviors/validation'
import { BuildField } from './items/field'
import { BuildMultiFile } from './items/field/multiFile'
import { AddressFormArea } from './items/addressFormArea'

interface FormWrapperProps {
  eventForm: IEventForm[]
  setIsNavigationBlocked: any
  formStateValues: any
  formFilesState: any
  requireDates: boolean
  setFilesSaved: React.Dispatch<React.SetStateAction<boolean>>
}

export const FormWrapper = ({
  eventForm,
  setIsNavigationBlocked,
  formFilesState,
  formStateValues,
  requireDates,
  setFilesSaved
}: FormWrapperProps): JSX.Element => {
  const today = new Date(new Date().toDateString())
  const isLaunched = formStateValues.isLaunched
  const isInPerson = (() => {
    const tab = eventForm.find(tab => tab.content.some(i => i.id === 'isInPerson'))
    if (!tab) return false
    const field = tab.content.find(i => i.id === 'isInPerson')
    return !!(field as IField)?.value
  })()
  const [dateRestrictions, setDateRestrictions] = useState({
    min: {
      startDate: null,
      endDate: null
    },
    max: {
      startDate: null,
      endDate: null
    }
  })

  const [formDateValidate, setformDateValidate] = useState({
    startDate: null,
    endDate: null
  })

  // Grupos de campos por seção
  const formSections = [
    {
      title: 'Informações Gerais',
      fields: [
        'isInPerson',
        'title',
        'description',
        'website',
        'coverImage',
        'homePageSlider',
        'supportFiles',
        'isActive'
      ]
    },
    {
      title: 'Data e Horário',
      fields: [
        'startDate',
        'endDate',
        'startTime',
        'endTime'
      ]
    },
    {
      title: 'Informações sobre ingresso',
      fields: [
        'isPaid',
        'ticketDescription'
      ]
    }
  ]

  useEffect(() => {
    initializeDataLimits(formStateValues, dateRestrictions)
  }, [])

  function fixDateLimits(selectedDate: string, previousDate: string, fieldName: string): void {
    limitDataFields(selectedDate, previousDate, fieldName, dateRestrictions, true)
    setDateRestrictions({ ...dateRestrictions })
  }

  const labels = {
    title: trans('Criar novo Evento'),
    edit: trans('Editar Evento'),
    saveAsDraft: trans('Salvar como rascunho'),
    saveAndLaunch: trans('Salvar e publicar')
  }

  const pageTitle = window.location.href.includes('/edit') ? labels.edit : labels.title

  useEffect(() => {
    setformDateValidate({ ...formStateValues })
  }, [])

  const startDate = new Date(`${formDateValidate.startDate} 00:00:00`)
  const isDateLocked = startDate <= new Date()

  return (
    <form>
      <FormEventWrapper>
        <FormChallengeHeader style={{ margin: '0px 0px 50px 0px' }}>
          <TitleAndButtonsWrapper>
            <TitleWrapper>
              <PageTitle>{pageTitle}</PageTitle>
            </TitleWrapper>
            <ButtonsWrapper>
              <SaveButton
                className="mr-2"
                setIsNavigationBlocked={setIsNavigationBlocked}
                eventForm={eventForm}
                text={isLaunched ? labels.saveAndLaunch : labels.saveAsDraft}
                formFilesState={formFilesState}
                formStateValues={formStateValues}
                launchAction={isLaunched}
                isLaunched={isLaunched}
                validateForm={() => {
                  return validateAll(isLaunched ? 'all' : 'save')
                }}
                setFilesSaved={setFilesSaved}
                data-cy={'eventSaveButton'}
              />
              {!isLaunched && (
                <SaveButton
                  formFilesState={formFilesState}
                  formStateValues={formStateValues}
                  eventForm={eventForm}
                  text={labels.saveAndLaunch}
                  setIsNavigationBlocked={setIsNavigationBlocked}
                  validateForm={() => {
                    return validateAll('all')
                  }}
                  launchAction
                  setFilesSaved={setFilesSaved}
                  data-cy={'eventPublishButton'}
                />
              )}
            </ButtonsWrapper>
          </TitleAndButtonsWrapper>
        </FormChallengeHeader>
        <CardWrapper>
          {formSections.map((section, sectionIdx) => {
            const sectionItems = eventForm
              .flatMap(tab => tab.content)
              .filter(item => section.fields.includes(item.id))
            if (sectionItems.length === 0) return null
            // layout para a seção "Data e Horário"
            if (section.title === 'Data e Horário') {
              const dateFields = sectionItems.filter(item => item.id === 'startDate' || item.id === 'endDate')
              const timeFields = sectionItems.filter(item => item.id === 'startTime' || item.id === 'endTime')
              return (
                <div key={section.title}>
                  <SectionTitle>{section.title}</SectionTitle>
                  <DateRow>
                    {dateFields.map((item, key) => {
                      let item1 = item as IField
                      if (requireDates) {
                        item1 = {
                          ...item1,
                          validations: [
                            {
                              type: 'required',
                              stage: 'save'
                            }
                          ]
                        }
                      }
                      return BuildField(
                        {
                          ...item1,
                          minDate: dateRestrictions.min[item.id],
                          maxDate: dateRestrictions.max[item.id],
                          updateFieldState: (field) => {
                            const previousDate = item1.value ?? ''
                            const tab = eventForm.find(t => t.content.some(i => i.id === item.id))
                            if (tab) {
                              const idx = tab.content.findIndex(i => i.id === item.id)
                              tab.content[idx] = field
                            }
                            fixDateLimits(field.value ?? '', previousDate, item.id)
                          },
                          disabled: false
                        },
                        key
                      )
                    })}
                  </DateRow>
                  <TimeRow>
                    {timeFields.map((item, key) =>
                      BuildField(
                        {
                          ...(item as IField),
                          updateFieldState: (field) => {
                            item = field
                            const tab = eventForm.find(t => t.content.some(i => i.id === item.id))
                            if (tab) {
                              const idx = tab.content.findIndex(i => i.id === item.id)
                              tab.content[idx] = field as IField
                            }
                          },
                          disabled: false
                        },
                        key
                      )
                    )}
                  </TimeRow>
                  <DividerLine style={{ margin: '32px 0' }} />
                </div>
              )
            }

            return (
              <div key={section.title}>
                <SectionTitle>{section.title}</SectionTitle>
                {sectionItems.map((item, key) => {
                  if (item.type === 'uploadImage' || item.type === 'uploadVideo') {
                    return BuildField(
                      {
                        ...item,
                        updateFieldState: (field) => {
                          item = field
                          const tab = eventForm.find(t => t.content.some(i => i.id === item.id))
                          if (tab) {
                            const idx = tab.content.findIndex(i => i.id === item.id)
                            tab.content[idx] = field as IFile
                          }
                        }
                      },
                      key
                    )
                  }
                  if (item.type === 'multiFile') {
                    return BuildMultiFile(
                      {
                        ...(item as IMultiFile),
                        updateFieldState: (field) => {
                          item = field
                          const tab = eventForm.find(t => t.content.some(i => i.id === item.id))
                          if (tab) {
                            const idx = tab.content.findIndex(i => i.id === item.id)
                            tab.content[idx] = field as IFile
                          }
                        }
                      },
                      key
                    )
                  }
                  if (item.type === 'datePicker') {
                    let item1 = item as IField
                    if (requireDates) {
                      item1 = {
                        ...item1,
                        validations: [
                          {
                            type: 'required',
                            stage: 'save'
                          }
                        ]
                      }
                    }
                    return BuildField(
                      {
                        ...item1,
                        minDate: dateRestrictions.min[item.id],
                        maxDate: dateRestrictions.max[item.id],
                        updateFieldState: (field) => {
                          const previousDate = item1.value ?? ''
                          const tab = eventForm.find(t => t.content.some(i => i.id === item.id))
                          if (tab) {
                            const idx = tab.content.findIndex(i => i.id === item.id)
                            tab.content[idx] = field
                          }
                          fixDateLimits(field.value ?? '', previousDate, item.id)
                        },
                        disabled: false
                      },
                      key
                    )
                  }
                  return BuildField(
                    {
                      ...(item as IField),
                      updateFieldState: (field) => {
                        item = field
                        const tab = eventForm.find(t => t.content.some(i => i.id === item.id))
                        if (tab) {
                          const idx = tab.content.findIndex(i => i.id === item.id)
                          tab.content[idx] = field as IField
                        }
                      },
                      disabled: false
                    },
                    key
                  )
                })}
                <DividerLine style={{ margin: '32px 0' }} />
              </div>
            )
          })}
          {(() => {
            const addressTab = eventForm.find(tab => tab.id === 'addressConfig')
            return AddressFormArea({ addressTab: addressTab as any, disabled: false, visible: isInPerson })
          })()}
        </CardWrapper>
      </FormEventWrapper>
    </form>
  )
}

function initializeDataLimits(formStateValues: IHubEventBasicFormState, dateRestrictions: any): void {
  if (formStateValues.startDate !== undefined && formStateValues.startDate !== null && formStateValues.startDate !== '') {
    limitDataFields(formStateValues.startDate ?? '', '', 'startDate', dateRestrictions, true)
  }
  if (
    formStateValues.endDate !== undefined &&
    formStateValues.endDate !== null &&
    formStateValues.endDate !== ''
  ) {
    limitDataFields(formStateValues.endDate ?? '', '', 'endDate', dateRestrictions, true)
  }
}
